import type { CSSProperties, HTMLAttributes, KeyboardEvent, MouseEvent } from 'react'
import { styled } from '@mui/material/styles'
import { FaHandPaper } from 'react-icons/fa'
import { SEVV_PRIMARY_CSS } from '../../theme/sevvDialogChrome'

/**
 * The one hand glyph for raise hand: the call bar, the presentation buttons and
 * every hand badge use it, so the control looks the same wherever it appears.
 */
export const HandIcon = ({ size }: { size?: number | string }) => (
  <FaHandPaper size={size} data-icon="raise-hand" aria-hidden focusable="false" />
)

/**
 * Bouncing raised-hand badge, shared by the presentation participants panel and
 * the office video tiles so a raised hand looks the same everywhere. Positioned
 * top-left by default; callers override placement with `style`.
 */
export const HandRaisedIndicator = styled('div')({
  position: 'absolute',
  top: '8px',
  left: '8px',
  backgroundColor: SEVV_PRIMARY_CSS,
  // Not --sevv-white: that is a surface colour (dark paper in dark mode); the icon needs contrast on primary in both.
  color: 'white',
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.2s ease',
  animation: 'bounce 1.2s infinite alternate',
  '@keyframes bounce': {
    from: { transform: 'translateY(0)' },
    to: { transform: 'translateY(-4px)' }
  },
  '&:hover': {
    transform: 'scale(1.1)',
  },
  '& svg': {
    width: '14px',
    height: '14px',
  },
})

/**
 * Props for any raised-hand badge. When the viewer may lower the hand (room
 * owner or presenter) the badge is a real control: a tab stop with a label that
 * Enter and Space activate as well as a click. Otherwise it is a labelled image.
 * Shared by the video tiles, the presentation participants panel and the
 * screen-share drawer so all three behave the same.
 */
export function handBadgeProps(
  onLower: (() => void) | undefined
): HTMLAttributes<HTMLElement> & { style: CSSProperties } {
  if (!onLower) {
    return { role: 'img', 'aria-label': 'Hand raised', title: 'Hand raised', style: { cursor: 'default' } }
  }
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': 'Lower hand',
    title: 'Click to lower hand',
    style: { cursor: 'pointer' },
    onClick: (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      onLower()
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      e.stopPropagation()
      onLower()
    },
  }
}

export default HandRaisedIndicator
